import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  apiUrl,
  createProduct,
  createProductFieldDefinition,
  deleteProduct,
  listProducts,
  updateProductFieldDefinition,
  updateProduct,
  listProductFieldDefinitions,
  listOrdersByMerchant,
  uploadMerchantProductImage,
} from "../services/api";
import { useMerchantStore } from "../store/useMerchantStore";
import { formatMoney } from "../utils/money";
import { ProductFieldDefinitionsPanel, keyFromLabel } from "../components/ProductFieldDefinitionsPanel";
import { EmptyState, FilterBar, PageHeader, SearchField } from "../components/orderKit";
import { productToOrderLineSku } from "../utils/productDisplay";
import * as XLSX from "xlsx";
import { validateMerchantPortalImageFile, safeProductOrProfileImageSrc } from "../utils/safeMerchantImageFile";
import { friendlyError } from "../utils/friendlyError";
import { t } from "../i18n";

const DEFAULT_PRODUCT_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
    <rect width="56" height="56" rx="8" fill="#fcfbfe"/>
    <path d="M12 40l10-12 8 9 6-7 8 10" fill="none" stroke="#8467c2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="20" cy="18" r="4" fill="#8467c2"/>
    <text x="28" y="51" text-anchor="middle" fill="#6a6480" font-size="8" font-family="Arial, sans-serif">No Image</text>
  </svg>`
)}`;

function catalogProductImageSrc(rawPathOrUrl) {
  if (!rawPathOrUrl || !String(rawPathOrUrl).trim()) return DEFAULT_PRODUCT_IMAGE;
  const raw = String(rawPathOrUrl).trim();
  const abs = raw.startsWith("http") ? raw : apiUrl(raw.startsWith("/") ? raw : `/${raw}`);
  return safeProductOrProfileImageSrc(abs, DEFAULT_PRODUCT_IMAGE);
}

const UNCATEGORIZED = "__none__";

/**
 * Products are grouped by the first "fixed list" (SELECT) custom field - e.g. "Type" → Food / Soft Drinks.
 * There is no separate category field; merchants model it as a custom field.
 */
function groupingField(fieldDefs) {
  return (
    [...(fieldDefs || [])]
      .filter((d) => d.fieldType === "SELECT")
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))[0] || null
  );
}

function productCategory(p, field) {
  if (!field) return null;
  const c = String(p?.extraAttributes?.[field.fieldKey] ?? "").trim();
  return c || null;
}

/**
 * Units sold per product id, counted when ordered (cancelled orders excluded).
 * Order lines carry the resolved `productName`; the line SKU is a fallback for older lines.
 */
function soldByProductId(products, orders) {
  const byName = new Map();
  const bySku = new Map();
  for (const p of products || []) {
    const name = String(p.name || "").trim().toLowerCase();
    if (name && !byName.has(name)) byName.set(name, p.id);
    for (const key of [p.sku, productToOrderLineSku(p)]) {
      const k = String(key || "").trim().toLowerCase();
      if (k && !bySku.has(k)) bySku.set(k, p.id);
    }
  }
  const sold = new Map();
  for (const o of orders || []) {
    if (String(o.status || "").toUpperCase() === "CANCELLED") continue;
    for (const item of o.items || []) {
      const id =
        byName.get(String(item.productName || "").trim().toLowerCase()) ??
        bySku.get(String(item.sku || "").trim().toLowerCase());
      if (id == null) continue;
      sold.set(id, (sold.get(id) || 0) + (Number(item.quantity) || 0));
    }
  }
  return sold;
}

function parseSelectOptions(raw) {
  if (!raw || typeof raw !== "string") return [];
  return raw
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function readWorksheetRows(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(t("catalog.read_failed")));
    reader.onload = (evt) => {
      try {
        const data = evt?.target?.result;
        const wb = XLSX.read(data, { type: "array" });
        const first = wb.SheetNames[0];
        if (!first) {
          reject(new Error(t("catalog.no_worksheet")));
          return;
        }
        const sheet = wb.Sheets[first];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        resolve(rows);
      } catch (err) {
        reject(new Error(t("catalog.invalid_xlsx")));
      }
    };
    reader.readAsArrayBuffer(file);
  });
}


function ProductForm({ merchantId, fieldDefs, product, onSave, onCancel, onManageFields }) {
  const [name, setName] = useState(product?.name ?? "");
  const [unitPrice, setUnitPrice] = useState(product?.unitPrice != null ? String(product.unitPrice) : "0.00");
  const [sku, setSku] = useState(product?.sku ?? "");
  const [imageUrl, setImageUrl] = useState(product?.imageUrl ?? "");
  const [extra, setExtra] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    setName(product?.name ?? "");
    setUnitPrice(product?.unitPrice != null ? String(product.unitPrice) : "0.00");
    setSku(product?.sku ?? "");
    setImageUrl(product?.imageUrl ?? "");
    setUploadError("");
    const fromProduct =
      product?.extraAttributes && typeof product.extraAttributes === "object" ? { ...product.extraAttributes } : {};
    const merged = { ...fromProduct };
    for (const d of fieldDefs) {
      if (merged[d.fieldKey] === undefined || merged[d.fieldKey] === null) {
        merged[d.fieldKey] = "";
      } else {
        merged[d.fieldKey] = String(merged[d.fieldKey]);
      }
    }
    setExtra(merged);
  }, [product, fieldDefs]);

  function setExtraField(key, value) {
    setExtra((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError(t("catalog.name_required"));
      return;
    }
    const priceNum = parseFloat(String(unitPrice).replace(",", "."));
    if (!Number.isFinite(priceNum)) {
      setError(t("catalog.price_number"));
      return;
    }
    const extraAttributes = {};
    for (const d of fieldDefs) {
      const raw = extra[d.fieldKey];
      if (raw != null && String(raw).trim() !== "") {
        extraAttributes[d.fieldKey] = String(raw).trim();
      }
    }
    try {
      setSubmitting(true);
      const data = {
        name: name.trim(),
        unitPrice: priceNum,
        sku: sku.trim() || undefined,
        imageUrl: imageUrl.trim() || undefined,
        extraAttributes,
      };
      if (product?.id) {
        await updateProduct(product.id, data);
      } else {
        await createProduct(merchantId, data);
      }
      onSave(product?.id ? t("catalog.updated") : t("catalog.added", { name: data.name }));
    } catch (err) {
      setError(friendlyError(err, t("catalog.save_failed")));
    } finally {
      setSubmitting(false);
    }
  }

  async function uploadImageFile(file) {
    if (!file) return;
    setUploadError("");
    try {
      setUploadingImage(true);
      const check = await validateMerchantPortalImageFile(file, { maxBytes: 10 * 1024 * 1024 });
      if (!check.ok) {
        setUploadError(check.message);
        return;
      }
      const uploaded = await uploadMerchantProductImage(merchantId, file);
      if (uploaded?.path) {
        setImageUrl(uploaded.path);
      } else {
        throw new Error(t("catalog.upload_no_path"));
      }
    } catch (err) {
      setUploadError(friendlyError(err, t("catalog.image_upload_failed")));
    } finally {
      setUploadingImage(false);
    }
  }

  async function handleImageFilePicked(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    await uploadImageFile(file);
  }

  return (
    <form onSubmit={submit} className="order-form">
      <div className="order-form__body">
        <section className="order-section">
          <div className="order-section__head">
            <span className="order-section__step">1</span>
            <div style={{ flex: 1 }}>
              <div className="order-section__title">{t("catalog.photo")}</div>
              <div className="order-section__hint">{t("catalog.photo_hint")}</div>
            </div>
          </div>
          <label
            className={`image-drop${dragOver ? " image-drop--over" : ""}${uploadingImage ? " image-drop--busy" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              uploadImageFile(e.dataTransfer.files?.[0]);
            }}
          >
            <img
              src={catalogProductImageSrc(imageUrl)}
              alt={t("catalog.preview_alt")}
              className="image-drop__preview"
              onError={(ev) => {
                ev.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
              }}
            />
            <div className="image-drop__copy">
              <div className="image-drop__title">
                <i className={`fa-solid ${uploadingImage ? "fa-spinner fa-spin" : "fa-cloud-arrow-up"}`} aria-hidden="true" />{" "}
                {uploadingImage ? t("catalog.uploading_image") : imageUrl ? t("catalog.replace_photo") : t("catalog.upload_photo")}
              </div>
              <div className="image-drop__hint">{t("catalog.drop_hint")}</div>
              {imageUrl ? (
                <button
                  type="button"
                  className="image-drop__remove"
                  onClick={(e) => {
                    e.preventDefault();
                    setImageUrl("");
                  }}
                >
                  {t("catalog.use_default_image")}
                </button>
              ) : null}
            </div>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,.heic,.heif"
              hidden
              disabled={uploadingImage}
              onChange={handleImageFilePicked}
            />
          </label>
          {uploadError ? <div className="field-error">{uploadError}</div> : null}
        </section>

        <section className="order-section">
          <div className="order-section__head">
            <span className="order-section__step">2</span>
            <div style={{ flex: 1 }}>
              <div className="order-section__title">{t("catalog.details")}</div>
              <div className="order-section__hint">{t("catalog.details_hint")}</div>
            </div>
          </div>
          <div className="product-form-grid">
            <div className="product-form-grid__wide">
              <label className="field-label" htmlFor="product-name">
                {t("catalog.name_label")}
              </label>
              <input
                id="product-name"
                placeholder={t("catalog.name_placeholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="product-price">
                {t("catalog.price_label")}
              </label>
              <div className="suffix-field">
                <input
                  id="product-price"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                />
                <span className="suffix-field__suffix">ETB</span>
              </div>
            </div>
            <div>
              <label className="field-label" htmlFor="product-sku">
                SKU <span className="muted" style={{ fontWeight: 400 }}>{t("catalog.optional")}</span>
              </label>
              <input id="product-sku" placeholder="e.g. SKU-001" value={sku} onChange={(e) => setSku(e.target.value)} />
            </div>
          </div>
        </section>

        <section className="order-section">
          <div className="order-section__head">
            <span className="order-section__step">3</span>
            <div style={{ flex: 1 }}>
              <div className="order-section__title">{t("catalog.custom_fields")}</div>
              <div className="order-section__hint">{t("catalog.custom_fields_hint")}</div>
            </div>
          </div>
          {fieldDefs.length > 0 ? (
            <div className="product-form-grid">
              {fieldDefs.map((d) => (
                <div key={d.id}>
                  <label className="field-label" htmlFor={`extra-${d.fieldKey}`}>
                    {d.label}
                  </label>
                  {d.fieldType === "SELECT" ? (
                    <select
                      id={`extra-${d.fieldKey}`}
                      value={extra[d.fieldKey] ?? ""}
                      onChange={(e) => setExtraField(d.fieldKey, e.target.value)}
                    >
                      <option value="">-</option>
                      {(d.options || []).map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={`extra-${d.fieldKey}`}
                      value={extra[d.fieldKey] ?? ""}
                      onChange={(e) => setExtraField(d.fieldKey, e.target.value)}
                      placeholder={d.label}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              {t("catalog.no_fields")}{" "}
              <button type="button" className="text-button" onClick={onManageFields}>
                {t("catalog.set_them_up")}
              </button>{" "}
              {t("catalog.no_fields_tail")}
            </p>
          )}
        </section>

        {error ? (
          <div className="alert alert--error">
            <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {error}
          </div>
        ) : null}
      </div>

      <div className="order-form__footer">
        <div>
          <div className="order-form__total-label">{product?.id ? t("catalog.editing") : t("catalog.new_product")}</div>
          <div className="order-form__total">
            {Number.isFinite(parseFloat(String(unitPrice).replace(",", ".")))
              ? t("common.amount_etb", { amount: formatMoney(parseFloat(String(unitPrice).replace(",", "."))) })
              : "-"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" onClick={onCancel} disabled={submitting}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="primary" disabled={submitting || uploadingImage}>
            {submitting ? t("store.saving") : product?.id ? t("catalog.save_product") : t("catalog.add_to_catalog")}
          </button>
        </div>
      </div>
    </form>
  );
}

const PRODUCT_SORTS = {
  name: { label: "catalog.sort.name", fn: (a, b) => String(a.name || "").localeCompare(String(b.name || "")) },
  bestSellers: { label: "catalog.sort.best", fn: (a, b, sold) => (sold.get(b.id) || 0) - (sold.get(a.id) || 0) },
  priceLow: { label: "catalog.sort.price_low", fn: (a, b) => Number(a.unitPrice || 0) - Number(b.unitPrice || 0) },
  priceHigh: { label: "catalog.sort.price_high", fn: (a, b) => Number(b.unitPrice || 0) - Number(a.unitPrice || 0) },
  newest: { label: "catalog.sort.newest", fn: (a, b) => Number(b.id || 0) - Number(a.id || 0) },
};

function readViewPref() {
  try {
    return localStorage.getItem("odos_catalog_view") === "list" ? "list" : "grid";
  } catch {
    return "grid";
  }
}

export function Catalog() {
  const { merchantId } = useMerchantStore();
  const [products, setProducts] = useState([]);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [activeSection, setActiveSection] = useState("products");
  const [error, setError] = useState("");
  const [importMessage, setImportMessage] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("name");
  const [view, setView] = useState(readViewPref);
  const [orders, setOrders] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const fieldImportInputRef = useRef(null);
  const productImportInputRef = useRef(null);

  const loadFieldDefs = useCallback(async () => {
    if (!merchantId) return;
    try {
      const list = await listProductFieldDefinitions(merchantId);
      setFieldDefs(list);
    } catch (_) {
      setFieldDefs([]);
    }
  }, [merchantId]);

  const refresh = useCallback(async () => {
    setError("");
    if (!merchantId) {
      setProducts([]);
      return;
    }
    setLoading(true);
    try {
      const [list, , orderList] = await Promise.all([
        listProducts(merchantId),
        loadFieldDefs(),
        // Sold counts come from orders; the catalog still works if they fail to load.
        listOrdersByMerchant(merchantId).catch(() => []),
      ]);
      setProducts(list);
      setOrders(Array.isArray(orderList) ? orderList : []);
    } catch (err) {
      setError(friendlyError(err, t("catalog.load_failed")));
    } finally {
      setLoading(false);
    }
  }, [merchantId, loadFieldDefs]);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  useEffect(() => {
    try {
      localStorage.setItem("odos_catalog_view", view);
    } catch {
      /* per-viewer convenience only */
    }
  }, [view]);

  const drawerOpen = activeSection === "products" && (showForm || editing);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setShowForm(false);
        setEditing(null);
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  // Close the Bulk (XLSX) menu when clicking anywhere outside it.
  useEffect(() => {
    const onDown = (e) => {
      document.querySelectorAll("details.menu[open]").forEach((d) => {
        if (!d.contains(e.target)) d.open = false;
      });
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    if (!importMessage) return undefined;
    const timer = setTimeout(() => setImportMessage(""), 5000);
    return () => clearTimeout(timer);
  }, [importMessage]);

  function closeForm() {
    setShowForm(false);
    setEditing(null);
  }

  async function handleDelete(p) {
    if (!window.confirm(t("catalog.remove_confirm", { name: p.name }))) return;
    try {
      await deleteProduct(p.id);
      await refresh();
      if (editing?.id === p.id) setEditing(null);
      setImportMessage(t("catalog.removed", { name: p.name }));
    } catch (err) {
      setError(friendlyError(err, t("catalog.delete_failed")));
    }
  }

  function handleSave(message) {
    closeForm();
    if (message) setImportMessage(message);
    refresh().catch(() => {});
  }

  function buildFieldDefsTemplateRows() {
    return (fieldDefs || []).map((d) => ({
      fieldKey: d.fieldKey,
      label: d.label,
      fieldType: d.fieldType || "TEXT",
      options: (d.options || []).join(", "),
      sortOrder: d.sortOrder ?? 0,
    }));
  }

  function buildProductTemplateRows() {
    const base = products.length > 0 ? products : [{ name: "", unitPrice: "", sku: "", imageUrl: "" }];
    return base.map((p) => {
      const row = {
        name: p.name || "",
        unitPrice: p.unitPrice ?? "",
        sku: p.sku || "",
        imageUrl: p.imageUrl || "",
      };
      for (const d of fieldDefs) {
        row[`custom_${d.fieldKey}`] = p.extraAttributes?.[d.fieldKey] ?? "";
      }
      return row;
    });
  }

  function downloadXlsx(rows, fileName, sheetName) {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, fileName);
  }

  function downloadFieldTemplate() {
    const rows = buildFieldDefsTemplateRows();
    if (rows.length === 0) {
      rows.push({
        fieldKey: "batch_number",
        label: "Batch number",
        fieldType: "TEXT",
        options: "",
        sortOrder: 0,
      });
    }
    downloadXlsx(rows, "product-field-definitions-template.xlsx", "FieldDefinitions");
  }

  function downloadProductTemplate() {
    const rows = buildProductTemplateRows();
    if (rows.length === 0) rows.push({ name: "", unitPrice: "", sku: "", imageUrl: "" });
    downloadXlsx(rows, "product-list-template.xlsx", "Products");
  }

  async function importFieldDefinitions(file) {
    if (!merchantId || !file) return;
    setError("");
    setImportMessage("");
    setImporting(true);
    try {
      const rows = await readWorksheetRows(file);
      if (!rows.length) throw new Error(t("catalog.no_rows"));
      const currentByKey = new Map(fieldDefs.map((d) => [String(d.fieldKey || "").toLowerCase(), d]));
      let processed = 0;
      for (const raw of rows) {
        const label = String(raw.label || "").trim();
        if (!label) continue;
        // Existing fields match by key; new ones get a key derived from the label (same rule as the form).
        const givenKey = String(raw.fieldKey || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "_");
        const key = currentByKey.has(givenKey)
          ? givenKey
          : keyFromLabel(label, [...currentByKey.keys()]);
        const fieldType = String(raw.fieldType || "TEXT").toUpperCase() === "SELECT" ? "SELECT" : "TEXT";
        const options = fieldType === "SELECT" ? parseSelectOptions(String(raw.options || "")) : [];
        const sortOrder = Number(raw.sortOrder) || 0;
        const existing = currentByKey.get(key);
        if (existing?.id) {
          await updateProductFieldDefinition(merchantId, existing.id, {
            fieldKey: existing.fieldKey,
            label,
            fieldType,
            options,
            sortOrder,
          });
        } else {
          await createProductFieldDefinition(merchantId, {
            fieldKey: key,
            label,
            fieldType,
            options,
            sortOrder,
          });
          currentByKey.set(key, { fieldKey: key });
        }
        processed += 1;
      }
      await refresh();
      setImportMessage(t("catalog.fields_imported", { n: processed }));
    } catch (err) {
      setError(friendlyError(err, t("catalog.fields_import_failed")));
    } finally {
      setImporting(false);
    }
  }

  async function importProducts(file) {
    if (!merchantId || !file) return;
    setError("");
    setImportMessage("");
    setImporting(true);
    try {
      const rows = await readWorksheetRows(file);
      if (!rows.length) throw new Error(t("catalog.no_rows"));
      let processed = 0;
      for (const raw of rows) {
        const name = String(raw.name || "").trim();
        if (!name) continue;
        const price = Number.parseFloat(String(raw.unitPrice || "0").replace(",", "."));
        if (!Number.isFinite(price)) continue;
        const sku = String(raw.sku || "").trim();
        const imageUrl = String(raw.imageUrl || "").trim();
        const extraAttributes = {};
        for (const d of fieldDefs) {
          const col = `custom_${d.fieldKey}`;
          const value = raw[col];
          if (value != null && String(value).trim() !== "") {
            extraAttributes[d.fieldKey] = String(value).trim();
          }
        }
        await createProduct(merchantId, {
          name,
          unitPrice: price,
          sku: sku || undefined,
          imageUrl: imageUrl || undefined,
          extraAttributes,
        });
        processed += 1;
      }
      await refresh();
      setImportMessage(t("catalog.products_imported", { n: processed }));
    } catch (err) {
      setError(friendlyError(err, t("catalog.products_import_failed")));
    } finally {
      setImporting(false);
    }
  }

  const sold = useMemo(() => soldByProductId(products, orders), [products, orders]);

  const groupField = useMemo(() => groupingField(fieldDefs), [fieldDefs]);

  /** Values of the grouping field in use, alphabetical (filter chips). */
  const categories = useMemo(() => {
    const seen = new Map();
    for (const p of products) {
      const c = productCategory(p, groupField);
      if (c && !seen.has(c.toLowerCase())) seen.set(c.toLowerCase(), c);
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [products, groupField]);

  const categoryOptions = useMemo(() => {
    const count = (c) =>
      products.filter((p) => (productCategory(p, groupField) || "").toLowerCase() === c.toLowerCase()).length;
    const none = products.filter((p) => !productCategory(p, groupField)).length;
    return [
      { id: "all", label: t("common.all"), count: products.length },
      ...categories.map((c) => ({ id: c, label: c, count: count(c) })),
      ...(none > 0 && categories.length > 0
        ? [{ id: UNCATEGORIZED, label: t("catalog.no_value", { field: String(groupField?.label || t("catalog.value")).toLowerCase() }), count: none }]
        : []),
    ];
  }, [products, categories, groupField]);

  const visibleProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = products.filter((p) => {
      const c = productCategory(p, groupField);
      if (categoryFilter === UNCATEGORIZED && c) return false;
      if (categoryFilter !== "all" && categoryFilter !== UNCATEGORIZED && (c || "").toLowerCase() !== categoryFilter.toLowerCase()) {
        return false;
      }
      if (!q) return true;
      return (
        String(p.name || "").toLowerCase().includes(q) ||
        String(p.sku || "").toLowerCase().includes(q) ||
        Object.values(p.extraAttributes || {}).some((v) => String(v ?? "").toLowerCase().includes(q))
      );
    });
    return [...list].sort((a, b) => PRODUCT_SORTS[sort].fn(a, b, sold));
  }, [products, search, sort, categoryFilter, sold, groupField]);

  const totalSold = useMemo(() => [...sold.values()].reduce((a, b) => a + b, 0), [sold]);

  if (!merchantId) {
    return (
      <div className="page-container">
        <div className="card">
          <p className="muted">Merchant session not loaded. Try signing out and signing in again with your contact email.</p>
        </div>
      </div>
    );
  }

  const openNew = () => {
    setActiveSection("products");
    setEditing(null);
    setShowForm(true);
  };

  return (
    <div className="page-container">
      <PageHeader
        eyebrow={t("nav.section.store")}
        title={t("nav.catalog")}
        lead={t("catalog.lead")}
        actions={
          <>
            <details className="menu">
              <summary className="menu__trigger">
                <i className={`fa-solid ${importing ? "fa-spinner fa-spin" : "fa-file-excel"}`} aria-hidden="true" /> {t("catalog.bulk")}{" "}
                <i className="fa-solid fa-chevron-down" aria-hidden="true" style={{ fontSize: 10 }} />
              </summary>
              <div
                className="menu__list"
                role="menu"
                onClick={(e) => {
                  if (e.target.closest("button")) e.currentTarget.closest("details").open = false;
                }}
              >
                <div className="menu__group">{t("form.products")}</div>
                <button type="button" role="menuitem" onClick={downloadProductTemplate}>
                  <i className="fa-solid fa-download" aria-hidden="true" /> {t("catalog.download_products_template")}
                </button>
                <button type="button" role="menuitem" onClick={() => productImportInputRef.current?.click()} disabled={importing}>
                  <i className="fa-solid fa-upload" aria-hidden="true" /> {t("catalog.upload_products")}
                </button>
                <div className="menu__group">{t("catalog.custom_fields")}</div>
                <button type="button" role="menuitem" onClick={downloadFieldTemplate}>
                  <i className="fa-solid fa-download" aria-hidden="true" /> {t("catalog.download_fields_template")}
                </button>
                <button type="button" role="menuitem" onClick={() => fieldImportInputRef.current?.click()} disabled={importing}>
                  <i className="fa-solid fa-upload" aria-hidden="true" /> {t("catalog.upload_fields")}
                </button>
              </div>
            </details>
            <button type="button" onClick={() => refresh()} disabled={loading}>
              <i className={`fa-solid fa-rotate-right${loading ? " fa-spin" : ""}`} aria-hidden="true" /> {t("common.refresh")}
            </button>
            <button type="button" className="primary" onClick={openNew}>
              <i className="fa-solid fa-plus" aria-hidden="true" /> {t("catalog.add_product")}
            </button>
          </>
        }
      />

      <input
        ref={productImportInputRef}
        type="file"
        accept=".xlsx"
        style={{ display: "none" }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) await importProducts(file);
          e.target.value = "";
        }}
      />
      <input
        ref={fieldImportInputRef}
        type="file"
        accept=".xlsx"
        style={{ display: "none" }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) await importFieldDefinitions(file);
          e.target.value = "";
        }}
      />

      {error ? (
        <div className="alert alert--error">
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {error}
        </div>
      ) : null}
      {importMessage ? (
        <div className="alert alert--success" role="status">
          <i className="fa-solid fa-circle-check" aria-hidden="true" /> {importMessage}
        </div>
      ) : null}

      <div className="list-toolbar">
        <div className="segmented" role="tablist" aria-label={t("catalog.section_aria")}>
          <button
            type="button"
            role="tab"
            aria-selected={activeSection === "products"}
            className={activeSection === "products" ? "segmented__on" : ""}
            onClick={() => setActiveSection("products")}
          >
            <i className="fa-solid fa-box-open" aria-hidden="true" /> {t("form.products")}{" "}
            <span className="segmented__count">{products.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeSection === "fields"}
            className={activeSection === "fields" ? "segmented__on" : ""}
            onClick={() => setActiveSection("fields")}
          >
            <i className="fa-solid fa-sliders" aria-hidden="true" /> {t("catalog.custom_fields")}{" "}
            <span className="segmented__count">{fieldDefs.length}</span>
          </button>
        </div>
        {activeSection === "products" && products.length > 0 ? (
          <div className="list-toolbar__right">
            <select className="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} aria-label={t("catalog.sort_aria")}>
              {Object.entries(PRODUCT_SORTS).map(([id, s]) => (
                <option key={id} value={id}>
                  {t(s.label)}
                </option>
              ))}
            </select>
            <div className="view-toggle" role="group" aria-label={t("catalog.view")}>
              <button
                type="button"
                className={view === "grid" ? "view-toggle__on" : ""}
                onClick={() => setView("grid")}
                aria-pressed={view === "grid"}
                title={t("catalog.grid_view")}
              >
                <i className="fa-solid fa-grip" aria-hidden="true" />
              </button>
              <button
                type="button"
                className={view === "list" ? "view-toggle__on" : ""}
                onClick={() => setView("list")}
                aria-pressed={view === "list"}
                title={t("catalog.list_view")}
              >
                <i className="fa-solid fa-list" aria-hidden="true" />
              </button>
            </div>
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder={t("catalog.search_placeholder")}
              ariaLabel={t("catalog.search_aria")}
            />
          </div>
        ) : null}
      </div>

      {activeSection === "products" && categories.length > 0 ? (
        <div className="list-toolbar" style={{ marginTop: -6 }}>
          <FilterBar
            options={categoryOptions}
            value={categoryFilter}
            onChange={setCategoryFilter}
            ariaLabel={groupField?.label || t("common.filter")}
          />
        </div>
      ) : null}

      {activeSection === "fields" ? (
        <ProductFieldDefinitionsPanel merchantId={merchantId} onChanged={loadFieldDefs} />
      ) : loading && products.length === 0 ? (
        <div className="loading">{t("catalog.loading")}</div>
      ) : products.length === 0 ? (
        <EmptyState
          icon="fa-box-open"
          title={t("catalog.empty_title")}
          message={t("catalog.empty_message")}
          action={
            <button type="button" className="primary" onClick={openNew}>
              <i className="fa-solid fa-plus" aria-hidden="true" /> {t("catalog.add_first")}
            </button>
          }
        />
      ) : (
        <>
          <p className="catalog-summary">
            {products.length === 1 ? t("catalog.one_product") : t("catalog.n_products", { n: products.length })}
            {" · "}
            {t("catalog.sold_total", { n: totalSold.toLocaleString() })}
            {search.trim() || categoryFilter !== "all" ? (
              <>
                {" · "}
                {t("catalog.showing", { n: visibleProducts.length })}
              </>
            ) : null}
          </p>

          {visibleProducts.length === 0 ? (
            <EmptyState
              icon="fa-magnifying-glass"
              title={t("catalog.no_matches")}
              message={search.trim() ? t("catalog.no_search_match", { q: search.trim() }) : t("catalog.no_filter_match")}
              action={
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setCategoryFilter("all");
                  }}
                >
                  {t("catalog.show_all")}
                </button>
              }
            />
          ) : view === "grid" ? (
            <div className="product-grid">
              {visibleProducts.map((p) => {
                return (
                  <article key={p.id} className="product-card">
                    <button
                      type="button"
                      className="product-card__media"
                      onClick={() => {
                        setEditing(p);
                        setShowForm(true);
                      }}
                      aria-label={t("catalog.edit_name", { name: p.name })}
                    >
                      <img
                        src={catalogProductImageSrc(p.imageUrl)}
                        alt=""
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                        }}
                      />
                    </button>
                    <div className="product-card__body">
                      {productCategory(p, groupField) ? (
                        <div className="product-card__category">{productCategory(p, groupField)}</div>
                      ) : null}
                      <div className="product-card__name" title={p.name}>
                        {p.name}
                      </div>
                      <div className="product-card__row">
                        <span className="product-card__price">{t("common.amount_etb", { amount: formatMoney(p.unitPrice) })}</span>
                        <span className={`sold-badge${(sold.get(p.id) || 0) === 0 ? " sold-badge--none" : ""}`}>
                          <i className="fa-solid fa-bag-shopping" aria-hidden="true" />{" "}
                          {t("catalog.n_sold", { n: (sold.get(p.id) || 0).toLocaleString() })}
                        </span>
                      </div>
                    </div>
                    <div className="product-card__actions">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(p);
                          setShowForm(true);
                        }}
                      >
                        <i className="fa-solid fa-pen" aria-hidden="true" /> {t("catalog.edit")}
                      </button>
                      <button
                        type="button"
                        className="icon-button icon-button--danger"
                        onClick={() => handleDelete(p)}
                        aria-label={t("catalog.delete_name", { name: p.name })}
                        title={t("catalog.delete")}
                      >
                        <i className="fa-regular fa-trash-can" aria-hidden="true" />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="table" style={{ minWidth: 760 }}>
                <thead>
                  <tr>
                    <th style={{ width: 72 }}>{t("catalog.col_image")}</th>
                    <th>{t("catalog.col_product")}</th>
                    {groupField ? <th>{groupField.label}</th> : null}
                    <th>{t("catalog.col_price")}</th>
                    <th>{t("catalog.col_sold")}</th>
                    <th style={{ width: 150 }}>{t("catalog.col_actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleProducts.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <img
                          src={catalogProductImageSrc(p.imageUrl)}
                          alt=""
                          style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 10, border: "1px solid var(--hairline)" }}
                          onError={(e) => {
                            e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
                          }}
                        />
                      </td>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      {groupField ? <td className="muted">{productCategory(p, groupField) || "-"}</td> : null}
                      <td>{t("common.amount_etb", { amount: formatMoney(p.unitPrice) })}</td>
                      <td style={{ fontWeight: 600 }}>{(sold.get(p.id) || 0).toLocaleString()}</td>
                      <td>
                        <div className="row" style={{ gap: 8 }}>
                          <button
                            type="button"
                            onClick={() => {
                              setEditing(p);
                              setShowForm(true);
                            }}
                          >
                            {t("catalog.edit")}
                          </button>
                          <button type="button" className="danger" onClick={() => handleDelete(p)}>
                            {t("catalog.delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {drawerOpen ? (
        <div className="drawer-backdrop" onMouseDown={(e) => e.target === e.currentTarget && closeForm()}>
          <aside className="drawer drawer--narrow" role="dialog" aria-modal="true" aria-labelledby="product-drawer-title">
            <header className="drawer__header">
              <div>
                <div className="eyebrow">{t("nav.catalog")}</div>
                <h2 id="product-drawer-title" style={{ margin: "4px 0 0" }}>
                  {editing?.id ? t("catalog.edit_name", { name: editing.name }) : t("catalog.new_product")}
                </h2>
              </div>
              <button type="button" className="icon-button" onClick={closeForm} aria-label={t("common.close")}>
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </header>
            <ProductForm
              merchantId={merchantId}
              fieldDefs={fieldDefs}
              product={editing}
              onSave={handleSave}
              onCancel={closeForm}
              onManageFields={() => {
                closeForm();
                setActiveSection("fields");
              }}
            />
          </aside>
        </div>
      ) : null}
    </div>
  );
}
